import {
  Button,
  Callout,
  Checkbox,
  Flex,
  Spinner,
  Text,
} from "@radix-ui/themes";

const positionsLabel = (count: number) =>
  `${count} open position${count === 1 ? "" : "s"}`;

export interface LiquidateAllSectionPresentationProps {
  positionCount: number;
  isLoadingPositions: boolean;
  /**
   * A failed positions read normalises to an empty book, so without this the
   * section would claim there is nothing to close on an account that has
   * positions. Blocks the action rather than acting on a book we cannot see.
   */
  positionsErrorMessage?: string;
  onRetryPositions: () => void;
  disabled?: boolean;
  onRequestLiquidate: () => void;
}

/**
 * Entry point for liquidate-all inside the risk-controls dialog. Kept visually
 * separate from the control checkboxes because it is an operation, not a flag:
 * nothing about it is applied by "Apply change".
 */
export const LiquidateAllSectionPresentation = ({
  positionCount,
  isLoadingPositions,
  positionsErrorMessage,
  onRetryPositions,
  disabled = false,
  onRequestLiquidate,
}: LiquidateAllSectionPresentationProps) => (
  <Flex
    direction="column"
    gap="2"
    mt="4"
    pt="4"
    className="border-[color:var(--gray-a5)] border-t"
  >
    <Flex align="center" justify="between" gap="3">
      <Flex direction="column" gap="1">
        <Text size="2" weight="medium">
          Liquidate all positions
        </Text>
        {positionsErrorMessage ? (
          <Text size="1" color="red">
            {positionsErrorMessage}{" "}
            <Button
              type="button"
              size="1"
              variant="ghost"
              onClick={onRetryPositions}
            >
              Retry
            </Button>
          </Text>
        ) : (
          <Text size="1" color="gray">
            {isLoadingPositions
              ? "Checking open positions…"
              : positionCount === 0
                ? "No open positions to close."
                : `Sends closing orders for ${positionsLabel(positionCount)}.`}
          </Text>
        )}
      </Flex>
      <Button
        type="button"
        variant="soft"
        color="red"
        disabled={
          disabled ||
          isLoadingPositions ||
          Boolean(positionsErrorMessage) ||
          positionCount === 0
        }
        onClick={onRequestLiquidate}
      >
        {isLoadingPositions ? <Spinner size="1" /> : null}
        Liquidate all
      </Button>
    </Flex>
  </Flex>
);

export interface LiquidateAllConfirmationPresentationProps {
  positionCount: number;
  liquidatedCount: number;
  cancelOrders: boolean;
  isPending: boolean;
  isSuccess: boolean;
  errorMessage?: string;
  onCancelOrdersChange: (next: boolean) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onBack: () => void;
}

/**
 * Owns its own action row: "Back" returns to the controls view rather than
 * dismissing the dialog, so the outer `AlertDialog.Cancel` footer is wrong here.
 */
export const LiquidateAllConfirmationPresentation = ({
  positionCount,
  liquidatedCount,
  cancelOrders,
  isPending,
  isSuccess,
  errorMessage,
  onCancelOrdersChange,
  onSubmit,
  onBack,
}: LiquidateAllConfirmationPresentationProps) => {
  if (isSuccess) {
    return (
      <Flex direction="column" gap="3" mt="4">
        <Callout.Root color="green" role="status">
          <Callout.Text>
            Closing orders submitted for {positionsLabel(liquidatedCount)}. They
            may take a moment to fill.
          </Callout.Text>
        </Callout.Root>
        <Flex gap="3" justify="end">
          <Button type="button" variant="soft" color="gray" onClick={onBack}>
            Back
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <Flex direction="column" gap="3" mt="4">
        <Text size="2">
          This immediately submits closing orders for every open position on
          this account ({positionsLabel(positionCount)}). It is not a draft
          ticket, and it can take a while to complete.
        </Text>

        <Text as="label" size="2" className="cursor-pointer select-none">
          <Flex align="center" gap="2">
            <Checkbox
              checked={cancelOrders}
              disabled={isPending}
              onCheckedChange={(next) => onCancelOrdersChange(next === true)}
            />
            Cancel open orders for these symbols
          </Flex>
        </Text>

        {errorMessage ? (
          <Callout.Root color="red" role="alert">
            <Callout.Text>{errorMessage}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3" justify="end">
          <Button
            type="button"
            variant="soft"
            color="gray"
            disabled={isPending}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            type="submit"
            color="red"
            disabled={isPending || positionCount === 0}
            loading={isPending}
          >
            Liquidate all
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
